/**
 * @voltxampere/engine — Diode DC stamp (Shockley with pnjlim limiting)
 *
 * SPECS §3.5 spells out the companion model:
 *
 *   Id  = Is · (exp(Vd / (n·Vt)) - 1)
 *   Geq = (Is / (n·Vt)) · exp(Vd / (n·Vt))
 *   Ieq = Id - Geq · Vd
 *
 * The stamp lays Geq into the four conductance cells (anode/cathode
 * pair) and adds Ieq to the RHS as a Norton-equivalent current
 * source. SPECS §3.6 gmin regularisation lives at the MNA-builder
 * layer (one diagonal injection per node); the stamp itself does
 * NOT add gmin to its Geq — doing so would double-count gmin on
 * diode-touched nodes (Sprint 5.1 P0 #1 fix).
 *
 * **Junction limiting** (Sprint 7 replaces Sprint 5 D3): Nagel's
 * pnjlim (1975 Appendix D) keeps `exp(Vd / (n·Vt))` finite and
 * Newton iterations continuous. The algorithm needs the *previous
 * iteration's limited voltage* as `vold`; the Newton driver owns a
 * `junctionHistory` map shared across iterations for exactly this
 * purpose (see `stamps/types.ts` and `dc/newton-raphson.ts`). Cold
 * start with an empty history uses `vold = 0`, which sends pnjlim
 * down its `Vt·log(vnew/Vt)` branch — the mathematically smooth
 * cold-start approximation.
 *
 * The stamp also clamps the per-iteration argument to `MAX_EXP_ARG`
 * as a hard ceiling. Pnjlim keeps iterates bounded once history
 * accumulates; the ceiling defends against the very first iteration
 * when the history is empty and the limiter has nothing to work
 * against.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
import { vtAtTemperature } from "./physical-constants.js";
import { computeVcrit, pnjlim } from "./pnjlim.js";
/**
 * Hard ceiling for the exponential argument. `exp(80) ≈ 5.5e34`
 * which is still finite in double precision; anything substantially
 * larger risks `Infinity`. Junction limiting keeps the *Newton*
 * step well-behaved; this hard clamp is the safety net for the very
 * first iteration before any limiting has happened.
 */
const MAX_EXP_ARG = 80;
/** Read `xPrev[idx]` with the ground-compression `-1` convention. */
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Key used by the diode stamp inside the Newton-driver's shared
 * pnjlim history. Combining the component id with a fixed
 * junction name leaves room for BJT / MOSFET stamps to register
 * multiple junctions against the same component.
 */
function junctionKey(componentId) {
    return `${componentId}:vd`;
}
export const diodeDCStamp = (ctx, component) => {
    const saturationCurrent = getNumericParam(component, "saturationCurrent", 1e-14);
    const ideality = getNumericParam(component, "ideality", 1);
    if (!(saturationCurrent > 0) || !Number.isFinite(saturationCurrent)) {
        throw new StampParameterError(`diodeDCStamp: component "${component.id}" has non-positive saturationCurrent (got ${String(saturationCurrent)})`, {
            meta: {
                stampType: "diode",
                parameter: "saturationCurrent",
                received: saturationCurrent,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    if (!(ideality > 0) || !Number.isFinite(ideality)) {
        throw new StampParameterError(`diodeDCStamp: component "${component.id}" has non-positive ideality (got ${String(ideality)})`, {
            meta: {
                stampType: "diode",
                parameter: "ideality",
                received: ideality,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    // Sprint 80.Z L-AYARLAR-5 — runtime temperature via stamp context.
    const nVt = ideality * vtAtTemperature(ctx.temperature);
    const anode = ctx.nodeIndex(getTerminalNetId(component, "anode"));
    const cathode = ctx.nodeIndex(getTerminalNetId(component, "cathode"));
    // Candidate junction voltage from the current Newton iterate.
    // First-pass xPrev is all zeros → vdRaw = 0 which drops us into
    // the OFF region of the model; subsequent iterations feed Nagel's
    // pnjlim to damp Newton overshoot.
    const vdRaw = nodeVoltage(ctx.xPrev, anode) - nodeVoltage(ctx.xPrev, cathode);
    const vcrit = computeVcrit(nVt, saturationCurrent);
    const key = junctionKey(component.id);
    // Empty history → cold start: pnjlim treats `vold = 0` as the
    // ascending branch and returns `nVt·log(vdRaw/nVt)` when vdRaw is
    // large. Once the iterate stabilises in the normal regime the
    // limiter is transparent — pnjlim short-circuits to `return vnew`.
    const vdPrevLimited = ctx.junctionHistory.get(key) ?? 0;
    const vdLimited = pnjlim(vdRaw, vdPrevLimited, nVt, vcrit, ctx.enableDeltaVDamping);
    ctx.junctionHistory.set(key, vdLimited);
    const arg = Math.min(vdLimited / nVt, MAX_EXP_ARG);
    const expTerm = Math.exp(arg);
    const id = saturationCurrent * (expTerm - 1);
    // NOTE: do NOT add `ctx.gmin` here. The MNA builder injects gmin
    // onto every node diagonal once (mna-builder.ts:103). Adding it
    // again inside the stamp would double-count the regularisation on
    // diode-touched diagonals AND distort the off-diagonal coupling
    // (the −geq cross terms would also pick up −gmin). The pre-fix
    // double-count was masked by the final gmin=0 pass washing it out
    // before the analytical assertions ran.
    const geq = (saturationCurrent / nVt) * expTerm;
    const ieq = id - geq * vdLimited;
    // Conductance stamp — symmetric four-corner pattern.
    ctx.addY(anode, anode, geq);
    ctx.addY(cathode, cathode, geq);
    ctx.addY(anode, cathode, -geq);
    ctx.addY(cathode, anode, -geq);
    // Norton-equivalent current. The companion model says
    // `i_branch = Geq · v_branch + Ieq`. To preserve the global KCL
    // sign convention (RHS = current INTO each node) we put `-Ieq`
    // at the anode and `+Ieq` at the cathode.
    if (anode >= 0)
        ctx.rhs[anode] = (ctx.rhs[anode] ?? 0) - ieq;
    if (cathode >= 0)
        ctx.rhs[cathode] = (ctx.rhs[cathode] ?? 0) + ieq;
};
/**
 * Companion model contributes only Geq + Ieq — no augmented row.
 *
 * Sprint 8a: anode and cathode nets carry a p-n junction voltage
 * (`Vd = V_anode - V_cathode`) so both are marked junction-bearing
 * for Newton step-size damping. Without damping, a large Newton
 * step across the `exp(V/Vt)` knee can overshoot into reverse bias
 * and collapse the admittance — see the PNP CE case study in
 * `SPRINT_8A_REPORT.md`.
 */
export const diodeDCStampDefinition = defineDCStamp("diode", diodeDCStamp, [], ["anode", "cathode"]);
//# sourceMappingURL=diode.js.map